<template>
  <div class="grid-dash-page">
    <div class="grid-dash-wrapper">
      <div v-if="gameState === 'MENU'" class="overlay">
        <h1>Grid Dash</h1>

        <p>
          Corre por la curva horaria del mercado eléctrico, esquiva desvíos y precios negativos,
          recoge MWh rentables, baterías y bonos de casación.<br>
          Usa ↑ para salto pequeño, → para salto grande y ↓ para agacharte.
        </p>

        <p v-if="modoPrueba" class="test-mode-msg">
          Modo prueba activo: no se guarda partida ni se da dinero real.
        </p>

        <button class="btn-start" @click="startGame">
          EMPEZAR
        </button>
      </div>

      <div v-if="gameState !== 'MENU'" class="hud">
        <div class="stat">
          MWh: {{ score }}
        </div>

        <div
          class="stat"
          :class="{ 'timer-low': timeLeft <= 10 }"
        >
          TIEMPO: {{ timeLeft }}s
        </div>

        <div
          class="stat battery shield-stat"
          :class="{ active: shieldCharges > 0 }"
        >
          <span
            class="hud-shield-icon"
            :class="{ active: shieldCharges > 0 }"
          >
            <span class="hud-shield-check"></span>
          </span>

          <span>
            ESCUDO: {{ shieldCharges }}
          </span>
        </div>
      </div>

      <div id="game-container" ref="gameContainer">
        <div
          v-if="gameState === 'GAMEOVER'"
          class="overlay result"
        >
          <h2>FIN DEL MERCADO</h2>

          <div class="final-score">
            {{ score }}
          </div>

          <p>MWh rentables capturados</p>

          <p
            v-if="verifiedScore !== null"
            style="color: #00ff44"
          >
            Partida validada correctamente
          </p>

          <p
            v-if="modoPrueba"
            class="test-mode-msg"
          >
            Modo prueba: esta partida no se ha guardado ni ha dado dinero real.
          </p>

          <p
            v-if="!modoPrueba && verifiedMoney !== null"
            style="color: #00ff44"
          >
            Dinero ganado:
            {{ Number(verifiedMoney).toLocaleString("es-ES") }} €
          </p>

          <button class="btn-start" @click="$emit('volver-menu')">VOLVER AL MENÚ</button>
        </div>
      </div>

      <div class="mobile-controls">
        <button
          class="ctrl-btn small-jump"
          @pointerdown.prevent="isSmallJumping = true"
          @pointerup.prevent="isSmallJumping = false"
          @pointercancel.prevent="isSmallJumping = false"
          @pointerleave="isSmallJumping = false"
          @contextmenu.prevent
        >
          ▲
        </button>

        <button
          class="ctrl-btn big-jump"
          @pointerdown.prevent="isBigJumping = true"
          @pointerup.prevent="isBigJumping = false"
          @pointercancel.prevent="isBigJumping = false"
          @pointerleave="isBigJumping = false"
          @contextmenu.prevent
        >
          ▶
        </button>

        <button
          class="ctrl-btn"
          @pointerdown.prevent="isDucking = true"
          @pointerup.prevent="isDucking = false"
          @pointercancel.prevent="isDucking = false"
          @pointerleave="isDucking = false"
          @contextmenu.prevent
        >
          ▼
        </button>
      </div>
    </div>

    <aside class="grid-dash-glossary">
      <div class="glossary-title">
        Glosario
      </div>

      <div class="glossary-section">
        <div class="glossary-section-title good-title">
          Bonos
        </div>

        <div class="glossary-item">
          <span class="glossary-icon icon-mwh">
            <span class="icon-mark mark-mwh"></span>
          </span>

          <div>
            <strong>MWh rentable</strong>
            <small>+2 puntos</small>
          </div>
        </div>

        <div class="glossary-item">
          <span class="glossary-icon icon-casacion">
            <span class="icon-mark mark-casacion"></span>
          </span>

          <div>
            <strong>Bono de casación</strong>
            <small>+5 puntos</small>
          </div>
        </div>

        <div class="glossary-item">
          <span class="glossary-icon icon-renewable">
            <span class="icon-mark mark-renewable"></span>
          </span>

          <div>
            <strong>Renovable</strong>
            <small>+3 puntos</small>
          </div>
        </div>

        <div class="glossary-item">
          <span class="glossary-icon icon-battery-shape">
            <span class="icon-mark mark-battery"></span>
          </span>

          <div>
            <strong>Batería</strong>
            <small>+1 carga de protección</small>
          </div>
        </div>

        <div class="glossary-item protection">
          <span class="glossary-icon icon-shield-shape">
            <span class="icon-mark mark-shield"></span>
          </span>

          <div>
            <strong>Escudo activo</strong>
            <small>Bloquea 1 obstáculo</small>
          </div>
        </div>
      </div>

      <div class="glossary-section">
        <div class="glossary-section-title bad-title">
          Obstáculos
        </div>

        <div class="glossary-item bad">
          <span class="glossary-icon icon-negative-price-shape">
            <span class="icon-mark mark-negative-price"></span>
          </span>

          <div>
            <strong>Precio negativo</strong>
            <small>-4 puntos y -2s</small>
          </div>
        </div>

        <div class="glossary-item bad">
          <span class="glossary-icon icon-imbalance-shape">
            <span class="icon-mark mark-imbalance"></span>
          </span>

          <div>
            <strong>Desvío</strong>
            <small>-5 puntos y -2s</small>
          </div>
        </div>

        <div class="glossary-item bad">
          <span class="glossary-icon icon-restriction-shape">
            <span class="icon-mark mark-restriction"></span>
          </span>

          <div>
            <strong>Restricción técnica</strong>
            <small>-4 puntos y -2s</small>
          </div>
        </div>
      </div>

      <div class="glossary-help">
        <strong>Consejo:</strong><br>
        Si el obstáculo va bajo, salta. Si va alto, agáchate.
      </div>
    </aside>
  </div>
</template>

<script>
import * as Phaser from "phaser";
import axios from "@/axios";
import CryptoJS from "crypto-js";
import { useGameStore } from "@/stores/game";
import Swal from "sweetalert2";

export default {
  name: "GridDash",

  data() {
    return {
      store: useGameStore(),

      modoPrueba: false,

      game: null,
      scene: null,
      gameState: "MENU",

      score: 0,
      timeLeft: 45,
      maxTime: 45,
      timerInterval: null,
      spawnEvent: null,

      idPartida: null,
      claveTemporal: null,
      verifiedScore: null,
      verifiedMoney: null,

      shieldCharges: 0,

      scrollSpeed: 180,
      minScrollSpeed: 180,
      maxScrollSpeed: 560,
      speedAcceleration: 9,

      isSmallJumping: false,
      isBigJumping: false,
      isDucking: false,
      jumpLocked: false,
      wasDucking: false,

      statsInternas: {
        startTime: null,
        totalTimeMs: 0,
        mwh_recogidos: 0,
        bonos_casacion: 0,
        baterias_recogidas: 0,
        renovables_recogidas: 0,
        obstaculos_esquivados: 0,
        choques: 0,
        saltos: 0,
        agachadas: 0,
      },
    };
  },

  mounted() {
    this.$nextTick(() => {
      this.initPhaser();
    });
  },

  beforeDestroy() {
    this.cleanupGame();
  },

  methods: {
    cleanupGame() {
      clearInterval(this.timerInterval);
      this.timerInterval = null;

      if (this.spawnEvent) {
        this.spawnEvent.remove(false);
        this.spawnEvent = null;
      }

      this.isSmallJumping = false;
      this.isBigJumping = false;
      this.isDucking = false;

      if (this.game) {
        this.game.destroy(true);
        this.game = null;
      }

      this.scene = null;
    },

    isPlayerDuckingNow() {
      const scene = this.scene;

      return !!(
        this.isDucking ||
        scene?.cursors?.down?.isDown
      );
    },

    cleanScene() {
      const scene = this.scene;

      if (!scene) return;

      if (this.spawnEvent) {
        this.spawnEvent.remove(false);
        this.spawnEvent = null;
      }

      if (scene.floorCollider) {
        scene.floorCollider.destroy();
        scene.floorCollider = null;
      }

      if (scene.bonusOverlap) {
        scene.bonusOverlap.destroy();
        scene.bonusOverlap = null;
      }

      if (scene.obstacleOverlap) {
        scene.obstacleOverlap.destroy();
        scene.obstacleOverlap = null;
      }

      if (scene.player) {
        scene.player.destroy();
        scene.player = null;
      }

      if (scene.floor) {
        scene.floor.destroy();
        scene.floor = null;
      }

      if (scene.obstacles) {
        scene.obstacles.clear(true, true);
        scene.obstacles.destroy();
        scene.obstacles = null;
      }

      if (scene.bonuses) {
        scene.bonuses.clear(true, true);
        scene.bonuses.destroy();
        scene.bonuses = null;
      }

      scene.tweens.killAll();
    },

    initPhaser() {
      const self = this;
      const container = this.$refs.gameContainer;

      if (!container) return;

      const config = {
        type: Phaser.AUTO,
        parent: container,
        pixelArt: true,
        width: container.clientWidth,
        height: container.clientHeight,
        backgroundColor: "#071827",

        physics: {
          default: "arcade",
          arcade: {
            gravity: {
              y: 1200,
            },
            debug: false,
          },
        },

        input: {
          keyboard: true,
          windowEvents: true,
        },

        scene: {
          preload() {
            this.load.spritesheet(
              "simulito_sprites",
              "minigames/gridDash/simulito-sprites.png",
              {
                frameWidth: 40,
                frameHeight: 40,
              }
            );

            const g = this.make.graphics({
              x: 0,
              y: 0,
              add: false,
            });

            g.fillStyle(0xf1c40f);
            g.fillRoundedRect(0, 8, 46, 46, 8);
            g.fillStyle(0x2c3e50);
            g.fillRect(30, 0, 8, 16);
            g.fillStyle(0xffffff);
            g.fillRect(8, 22, 8, 8);
            g.fillRect(20, 22, 8, 8);
            g.generateTexture("player", 46, 54);
            g.clear();

            g.fillStyle(0x2ecc71);
            g.fillCircle(16, 16, 16);
            g.fillStyle(0xffffff);
            g.fillRect(14, 7, 4, 18);
            g.fillRect(7, 14, 18, 4);
            g.generateTexture("mwh", 32, 32);
            g.clear();

            g.fillStyle(0xf1c40f);
            g.fillTriangle(18, 0, 36, 18, 18, 36);
            g.fillTriangle(18, 0, 0, 18, 18, 36);
            g.fillStyle(0xffffff);
            g.fillRect(16, 9, 4, 18);
            g.fillRect(9, 16, 18, 4);
            g.generateTexture("casacion", 36, 36);
            g.clear();

            g.fillStyle(0x3498db);
            g.fillRoundedRect(0, 6, 38, 24, 5);
            g.fillStyle(0x85c1e9);
            g.fillRect(38, 13, 5, 10);
            g.fillStyle(0xffffff);
            g.fillRect(17, 11, 4, 14);
            g.fillRect(12, 16, 14, 4);
            g.generateTexture("battery", 44, 36);
            g.clear();

            g.fillStyle(0x00ff99);
            g.fillCircle(18, 18, 16);
            g.fillStyle(0xffffff);
            g.fillRect(16, 9, 4, 18);
            g.fillRect(9, 16, 18, 4);
            g.generateTexture("renewable", 36, 36);
            g.clear();

            g.fillStyle(0xe53935);
            g.fillTriangle(22, 0, 44, 44, 0, 44);
            g.fillStyle(0xffffff);
            g.fillRect(13, 30, 18, 4);
            g.generateTexture("negative_price", 44, 44);
            g.clear();

            g.fillStyle(0xc62828);
            g.fillRoundedRect(0, 0, 56, 34, 6);
            g.fillStyle(0xffffff);
            g.fillRect(14, 15, 28, 5);
            g.generateTexture("restriction", 56, 34);
            g.clear();

            g.fillStyle(0xff1744);
            g.fillRoundedRect(0, 0, 42, 42, 8);
            g.fillStyle(0xffffff);
            g.fillRect(11, 19, 20, 5);
            g.generateTexture("imbalance", 42, 42);
            g.clear();
          },

          create() {
            self.scene = this;

            const W = container.clientWidth;
            const H = container.clientHeight;

            this.physics.world.setBounds(
              0,
              0,
              W,
              H
            );

            const bg = this.add.graphics();

            bg.fillStyle(0x071827, 1);
            bg.fillRect(
              0,
              0,
              W,
              H
            );

            bg.lineStyle(
              1,
              0x15384f,
              0.7
            );

            for (
              let x = 0;
              x < W;
              x += 40
            ) {
              bg.lineBetween(
                x,
                0,
                x,
                H
              );
            }

            for (
              let y = 0;
              y < H;
              y += 40
            ) {
              bg.lineBetween(
                0,
                y,
                W,
                y
              );
            }

            bg.lineStyle(
              3,
              0x00d4ff,
              0.8
            );

            bg.beginPath();
            bg.moveTo(
              0,
              H - 220
            );

            for (
              let x = 0;
              x <= W;
              x += 40
            ) {
              const y =
                H -
                220 +
                Math.sin(x / 45) *
                  25;

              bg.lineTo(
                x,
                y
              );
            }

            bg.strokePath();

            this.add.text(
              16,
              H - 60,
              "MERCADO DIARIO · OMIE RUN",
              {
                fontFamily: "Arial",
                fontSize: "13px",
                color: "#8adfff",
              }
            );

            this.cursors =
              this.input.keyboard
                .createCursorKeys();

            this.spaceKey =
              this.input.keyboard.addKey(
                Phaser.Input.Keyboard
                  .KeyCodes.SPACE
              );

            this.anims.create({
              key: "parado",
              frames:
                this.anims
                  .generateFrameNumbers(
                    "simulito_sprites",
                    {
                      start: 0,
                      end: 2,
                    }
                  ),
              frameRate: 10,
              repeat: -1,
            });

            this.anims.create({
              key: "agachado",
              frames: [
                {
                  key:
                    "simulito_sprites",
                  frame: 3,
                },
              ],
              frameRate: 1,
            });

            this.anims.create({
              key: "salto",
              frames: [
                {
                  key:
                    "simulito_sprites",
                  frame: 4,
                },
              ],
              frameRate: 1,
            });

            this.anims.create({
              key: "tocado",
              frames: [
                {
                  key:
                    "simulito_sprites",
                  frame: 5,
                },
              ],
              frameRate: 1,
            });
          },

          update(time, delta) {
            if (
              self.gameState ===
              "PLAYING"
            ) {
              self.updateGameLogic(
                this,
                time,
                delta
              );
            }
          },
        },
      };

      this.game =
        new Phaser.Game(config);
    },

    createWorld() {
      const scene = this.scene;

      if (!scene) return;

      const W =
        this.game.config.width;

      const H =
        this.game.config.height;

      const groundY =
        H - 95;

      scene.floor =
        scene.add.rectangle(
          W / 2,
          groundY + 38,
          W,
          76,
          0x1b2f3f
        );

      scene.physics.add.existing(
        scene.floor,
        true
      );

      scene.add.rectangle(
        W / 2,
        groundY,
        W,
        4,
        0x00d4ff,
        0.8
      );

      scene.player =
        scene.physics.add.sprite(
          95,
          groundY - 75,
          "simulito_sprites"
        );

      scene.player.setDisplaySize(
        150,
        150
      );

      const escalaDeseada =
        scene.player.scaleX;

      scene.player.setData(
        "normalScaleX",
        escalaDeseada
      );

      scene.player.setData(
        "normalScaleY",
        escalaDeseada
      );

      scene.player.setData(
        "hasShield",
        false
      );

      scene.player.setCollideWorldBounds(
        true
      );

      scene.player.setBounce(0);

      scene.player.setData(
        "normalBody",
        {
          width:
            scene.player.width *
            0.5,

          height:
            scene.player.height *
            0.85,

          offsetX:
            scene.player.width *
            0.25,

          offsetY:
            scene.player.height *
            0.15,
        }
      );

      scene.player.setData(
        "duckBody",
        {
          width:
            scene.player.width *
            0.55,

          height:
            scene.player.height *
            0.45,

          offsetX:
            scene.player.width *
            0.22,

          offsetY:
            scene.player.height *
            0.55,
        }
      );

      const normalBody =
        scene.player.getData(
          "normalBody"
        );

      scene.player.body.setSize(
        normalBody.width,
        normalBody.height
      );

      scene.player.body.setOffset(
        normalBody.offsetX,
        normalBody.offsetY
      );

      scene.floorCollider =
        scene.physics.add.collider(
          scene.player,
          scene.floor
        );

      scene.obstacles =
        scene.physics.add.group({
          allowGravity: false,
          immovable: true,
        });

      scene.bonuses =
        scene.physics.add.group({
          allowGravity: false,
          immovable: true,
        });

      scene.bonusOverlap =
        scene.physics.add.overlap(
          scene.player,
          scene.bonuses,
          (player, bonus) => {
            this.collectBonus(
              bonus
            );
          }
        );

      scene.obstacleOverlap =
        scene.physics.add.overlap(
          scene.player,
          scene.obstacles,
          (player, obstacle) => {
            this.hitObstacle(
              obstacle
            );
          }
        );
    },

    updateGameLogic(
      scene,
      time,
      delta
    ) {
      if (
        !scene.player ||
        !scene.player.active ||
        !scene.player.body
      ) {
        return;
      }

      const hasShield =
        this.shieldCharges > 0;

      const previousShield =
        scene.player.getData(
          "hasShield"
        );

      if (
        hasShield !==
        previousShield
      ) {
        scene.player.setData(
          "hasShield",
          hasShield
        );

        if (hasShield) {
          scene.player.setTint(
            0xffff00
          );
        } else {
          scene.player.clearTint();
        }
      }

      this.scrollSpeed =
        Math.min(
          this.maxScrollSpeed,
          this.scrollSpeed +
            (delta / 1000) *
              this.speedAcceleration
        );

      const smallJumpPressed =
        scene.cursors.up.isDown ||
        this.isSmallJumping;

      const bigJumpPressed =
        scene.cursors.right.isDown ||
        this.isBigJumping;

      const jumpPressed =
        smallJumpPressed ||
        bigJumpPressed;

      const duckPressed =
        scene.cursors.down.isDown ||
        this.isDucking;

      const isGrounded =
        scene.player.body.blocked.down;

      if (
        jumpPressed &&
        !this.jumpLocked &&
        isGrounded &&
        !duckPressed
      ) {
        const jumpForce =
          bigJumpPressed
            ? -760
            : -540;

        scene.player.setVelocityY(
          jumpForce
        );

        this.statsInternas.saltos++;

        this.jumpLocked = true;

        this.showFloatingText(
          scene,
          scene.player.x,
          scene.player.y - 50,
          bigJumpPressed
            ? "SALTO GRANDE"
            : "SALTO",
          bigJumpPressed
            ? "#00d4ff"
            : "#ffffff"
        );
      }

      if (!jumpPressed) {
        this.jumpLocked = false;
      }

      const normalBody =
        scene.player.getData(
          "normalBody"
        );

      const duckBody =
        scene.player.getData(
          "duckBody"
        );

      const shouldDuck =
        duckPressed &&
        isGrounded;

      if (
        shouldDuck !==
        this.wasDucking
      ) {
        if (shouldDuck) {
          if (duckBody) {
            scene.player.body.setSize(
              duckBody.width,
              duckBody.height
            );

            scene.player.body.setOffset(
              duckBody.offsetX,
              duckBody.offsetY
            );
          }

          this.statsInternas
            .agachadas++;
        } else {
          if (normalBody) {
            scene.player.body.setSize(
              normalBody.width,
              normalBody.height
            );

            scene.player.body.setOffset(
              normalBody.offsetX,
              normalBody.offsetY
            );
          }
        }

        this.wasDucking =
          shouldDuck;
      }

      const currentAnimation =
        scene.player.anims
          .currentAnim?.key;

      const hitAnimationPlaying =
        currentAnimation ===
          "tocado" &&
        scene.player.anims
          .isPlaying;

      if (!hitAnimationPlaying) {
        let nextAnimation;

        if (shouldDuck) {
          nextAnimation =
            "agachado";
        } else if (
          !isGrounded
        ) {
          nextAnimation =
            "salto";
        } else {
          nextAnimation =
            "parado";
        }

        if (
          currentAnimation !==
          nextAnimation
        ) {
          scene.player.anims.play(
            nextAnimation,
            true
          );
        }
      }

      if (scene.obstacles) {
        const obstacles = [
          ...scene.obstacles
            .getChildren(),
        ];

        for (
          const obstacle of
          obstacles
        ) {
          if (
            !obstacle ||
            !obstacle.active
          ) {
            continue;
          }

          obstacle.setVelocityX(
            -this.scrollSpeed
          );

          if (
            obstacle.x <
            -80
          ) {
            if (
              !obstacle.getData(
                "hit"
              )
            ) {
              this.statsInternas
                .obstaculos_esquivados++;

              this.score += 1;
            }

            obstacle.destroy();
          }
        }
      }

      if (scene.bonuses) {
        const bonuses = [
          ...scene.bonuses
            .getChildren(),
        ];

        for (
          const bonus of
          bonuses
        ) {
          if (
            !bonus ||
            !bonus.active
          ) {
            continue;
          }

          bonus.setVelocityX(
            -this.scrollSpeed
          );

          if (
            bonus.x <
            -80
          ) {
            bonus.destroy();
          }
        }
      }
    },

    hitObstacle(obstacle) {
      if (
        !obstacle ||
        !obstacle.active ||
        obstacle.getData("hit")
      ) {
        return;
      }

      const scene = this.scene;

      if (
        !scene ||
        !scene.player
      ) {
        return;
      }

      const lane =
        obstacle.getData("lane");

      const isHighObstacle =
        lane === "high";

      const isDucking =
        this.isPlayerDuckingNow();

      if (
        isHighObstacle &&
        isDucking
      ) {
        obstacle.setData(
          "hit",
          true
        );

        this.statsInternas
          .obstaculos_esquivados++;

        this.score += 1;

        this.showFloatingText(
          scene,
          obstacle.x,
          obstacle.y - 30,
          "AGACHADO",
          "#00d4ff"
        );

        obstacle.destroy();

        return;
      }

      obstacle.setData(
        "hit",
        true
      );

      const penalty =
        obstacle.getData(
          "penalty"
        ) || 4;

      const label =
        obstacle.getData(
          "label"
        ) ||
        "Penalización";

      if (
        this.shieldCharges > 0
      ) {
        this.shieldCharges--;

        this.showFloatingText(
          scene,
          obstacle.x,
          obstacle.y - 30,
          "ESCUDO",
          "#3498db"
        );
      } else {
        this.statsInternas.choques++;

        this.score =
          Math.max(
            0,
            this.score -
              penalty
          );

        this.timeLeft =
          Math.max(
            0,
            this.timeLeft - 2
          );

        if (
          scene.player?.active
        ) {
          scene.player.anims.play(
            "tocado",
            true
          );
        }

        this.showFloatingText(
          scene,
          obstacle.x,
          obstacle.y - 30,
          `-${penalty} ${label}`,
          "#ff4d4d"
        );

        if (
          scene.cameras &&
          scene.cameras.main
        ) {
          scene.cameras.main.shake(
            120,
            0.01
          );
        }
      }

      obstacle.destroy();

      if (
        this.timeLeft <= 0
      ) {
        this.endGame();
      }
    },

    spawnMarketElement() {
      if (
        this.gameState !==
          "PLAYING" ||
        !this.scene
      ) {
        return;
      }

      const scene = this.scene;

      if (
        !scene.obstacles ||
        !scene.bonuses
      ) {
        return;
      }

      const W =
        this.game.config.width;

      const H =
        this.game.config.height;

      const groundY =
        H - 95;

      const roll =
        Math.random();

      if (roll < 0.48) {
        this.spawnObstacle(
          scene,
          W,
          groundY
        );
      } else {
        this.spawnBonus(
          scene,
          W,
          groundY
        );
      }
    },

    spawnObstacle(
      scene,
      W,
      groundY
    ) {
      const obstacleLanes = [
        {
          name: "low",
          y:
            groundY - 22,
          avoid: "Saltar",
          bodyWidth: 38,
          bodyHeight: 38,
        },
        {
          name: "high",
          y:
            groundY - 65,
          avoid: "Agacharse",
          bodyWidth: 62,
          bodyHeight: 34,
        },
      ];

      const types = [
        {
          key:
            "negative_price",
          label:
            "Precio negativo",
          pointsPenalty: 4,
        },
        {
          key: "imbalance",
          label: "Desvío",
          pointsPenalty: 5,
        },
        {
          key: "restriction",
          label:
            "Restricción técnica",
          pointsPenalty: 4,
        },
      ];

      const type =
        types[
          Math.floor(
            Math.random() *
              types.length
          )
        ];

      const lane =
        obstacleLanes[
          Math.floor(
            Math.random() *
              obstacleLanes.length
          )
        ];

      const obstacle =
        scene.obstacles.create(
          W + 60,
          lane.y,
          type.key
        );

      obstacle.body.allowGravity =
        false;

      obstacle.setVelocityX(
        -this.scrollSpeed
      );

      obstacle.setData(
        "label",
        type.label
      );

      obstacle.setData(
        "penalty",
        type.pointsPenalty
      );

      obstacle.setData(
        "hit",
        false
      );

      obstacle.setData(
        "lane",
        lane.name
      );

      obstacle.setData(
        "avoid",
        lane.avoid
      );

      obstacle.body.setSize(
        lane.bodyWidth,
        lane.bodyHeight
      );
    },

    spawnBonus(
      scene,
      W,
      groundY
    ) {
      const bonusLanes = [
        {
          name: "low",
          y:
            groundY - 35,
        },
        {
          name: "middle",
          y:
            groundY - 95,
        },
        {
          name: "high",
          y:
            groundY - 150,
        },
      ];

      const types = [
        {
          key: "mwh",
          label: "+2 MWh",
          value: 2,
          stat:
            "mwh_recogidos",
          color: "#2ecc71",
        },
        {
          key: "casacion",
          label:
            "+5 Casación",
          value: 5,
          stat:
            "bonos_casacion",
          color: "#f1c40f",
        },
        {
          key: "battery",
          label: "Batería",
          value: 0,
          stat:
            "baterias_recogidas",
          color: "#3498db",
        },
        {
          key: "renewable",
          label:
            "+3 Renovable",
          value: 3,
          stat:
            "renovables_recogidas",
          color: "#00ff99",
        },
      ];

      const type =
        types[
          Math.floor(
            Math.random() *
              types.length
          )
        ];

      const lane =
        bonusLanes[
          Math.floor(
            Math.random() *
              bonusLanes.length
          )
        ];

      const bonus =
        scene.bonuses.create(
          W + 60,
          lane.y,
          type.key
        );

      bonus.body.allowGravity =
        false;

      bonus.setVelocityX(
        -this.scrollSpeed
      );

      bonus.setData(
        "label",
        type.label
      );

      bonus.setData(
        "value",
        type.value
      );

      bonus.setData(
        "stat",
        type.stat
      );

      bonus.setData(
        "lane",
        lane.name
      );

      bonus.setData(
        "color",
        type.color
      );

      bonus.body.setSize(
        30,
        30
      );
    },

    collectBonus(bonus) {
      if (
        !bonus ||
        !bonus.active
      ) {
        return;
      }

      const scene =
        this.scene;

      if (!scene) return;

      const value =
        bonus.getData(
          "value"
        ) || 0;

      const stat =
        bonus.getData(
          "stat"
        );

      const label =
        bonus.getData(
          "label"
        );

      if (
        stat ===
        "baterias_recogidas"
      ) {
        this.shieldCharges =
          Math.min(
            2,
            this.shieldCharges +
              1
          );

        this.statsInternas
          .baterias_recogidas++;
      } else {
        this.score += value;

        if (
          stat ===
          "mwh_recogidos"
        ) {
          this.statsInternas
            .mwh_recogidos +=
            value;
        } else if (
          stat ===
          "bonos_casacion"
        ) {
          this.statsInternas
            .bonos_casacion++;
        } else if (
          stat ===
          "renovables_recogidas"
        ) {
          this.statsInternas
            .renovables_recogidas++;
        }
      }

      const color =
        bonus.getData(
          "color"
        ) ||
        "#00ff99";

      this.showFloatingText(
        scene,
        bonus.x,
        bonus.y - 25,
        label,
        color
      );

      bonus.destroy();
    },

    showFloatingText(
      scene,
      x,
      y,
      text,
      color
    ) {
      if (
        !scene ||
        !scene.sys?.isActive()
      ) {
        return;
      }

      const label =
        scene.add.text(
          x,
          y,
          text,
          {
            fontFamily: "Arial",
            fontSize: "14px",
            color,
            fontStyle: "bold",
            stroke: "#000000",
            strokeThickness: 3,
          }
        );

      scene.tweens.add({
        targets: label,
        y: y - 35,
        alpha: 0,
        duration: 700,

        onComplete: () => {
          if (
            label &&
            label.active
          ) {
            label.destroy();
          }
        },
      });
    },

    async startGame() {
      try {
        if (this.modoPrueba) {
          this.idPartida = null;
          this.claveTemporal = null;
        } else {
          const response =
            await axios.post(
              "/api/minigames/partida/iniciar",
              {
                game_id:
                  "grid_dash",
              }
            );

          if (
            response.data.status !==
            "success"
          ) {
            return;
          }

          this.idPartida =
            response.data.data
              .id_partida;

          this.claveTemporal =
            response.data.data
              .clave_temporal;
        }

        this.gameState =
          "PLAYING";

        this.score = 0;
        this.timeLeft =
          this.maxTime;

        this.verifiedScore =
          null;

        this.verifiedMoney =
          null;

        this.shieldCharges =
          0;

        this.scrollSpeed =
          this.minScrollSpeed;

        this.isSmallJumping =
          false;

        this.isBigJumping =
          false;

        this.isDucking =
          false;

        this.jumpLocked =
          false;

        this.wasDucking =
          false;

        this.statsInternas = {
          startTime:
            Date.now(),

          totalTimeMs: 0,

          mwh_recogidos: 0,

          bonos_casacion: 0,

          baterias_recogidas: 0,

          renovables_recogidas: 0,

          obstaculos_esquivados: 0,

          choques: 0,

          saltos: 0,

          agachadas: 0,
        };

        clearInterval(
          this.timerInterval
        );

        this.timerInterval =
          null;

        const scene =
          this.scene;

        if (!scene) {
          throw new Error(
            "La escena de Phaser no está inicializada"
          );
        }

        scene.physics.resume();

        this.cleanScene();
        this.createWorld();

        if (
          this.game &&
          this.game.canvas
        ) {
          this.game.canvas.focus();
        }

        this.spawnEvent =
          scene.time.addEvent({
            delay: 850,

            callback:
              this.spawnMarketElement,

            callbackScope:
              this,

            loop: true,
          });

        this.timerInterval =
          setInterval(() => {
            this.timeLeft--;

            if (
              this.timeLeft <=
              0
            ) {
              this.endGame();
            }
          }, 1000);
      } catch (error) {
        console.error(
          "Error iniciando partida",
          error
        );

        const msg =
          error?.response?.data
            ?.message ||
          error?.response?.data
            ?.data?.message ||
          error?.message ||
          "No se pudo iniciar la partida.";

        await Swal.fire({
          icon: "info",
          title:
            "Minijuego no disponible",
          text: msg,
          confirmButtonText:
            "Entendido",

          customClass: {
            confirmButton:
              "clausulazo-swal-confirm",
          },
        });

        this.gameState =
          "MENU";
      }
    },

    async endGame() {
      if (
        this.gameState ===
        "GAMEOVER"
      ) {
        return;
      }

      this.gameState =
        "GAMEOVER";

      clearInterval(
        this.timerInterval
      );

      this.timerInterval =
        null;

      if (this.spawnEvent) {
        this.spawnEvent.remove(
          false
        );

        this.spawnEvent =
          null;
      }

      this.isSmallJumping =
        false;

      this.isBigJumping =
        false;

      this.isDucking =
        false;

      if (this.scene) {
        this.scene.physics.pause();

        if (
          this.scene.player
        ) {
          this.scene.player.destroy();
          this.scene.player =
            null;
        }

        if (
          this.scene.obstacles
        ) {
          this.scene.obstacles.clear(
            true,
            true
          );
        }

        if (
          this.scene.bonuses
        ) {
          this.scene.bonuses.clear(
            true,
            true
          );
        }
      }

      this.statsInternas.totalTimeMs =
        Date.now() -
        this.statsInternas
          .startTime;

      const stats = {
        mwh_recogidos:
          this.statsInternas
            .mwh_recogidos,

        bonos_casacion:
          this.statsInternas
            .bonos_casacion,

        baterias_recogidas:
          this.statsInternas
            .baterias_recogidas,

        renovables_recogidas:
          this.statsInternas
            .renovables_recogidas,

        obstaculos_esquivados:
          this.statsInternas
            .obstaculos_esquivados,

        choques:
          this.statsInternas
            .choques,

        saltos:
          this.statsInternas
            .saltos,

        agachadas:
          this.statsInternas
            .agachadas,

        tiempo_jugado_ms:
          this.statsInternas
            .totalTimeMs,

        puntos_locales:
          this.score,
      };

      await this.finalizarPartida(
        stats
      );
    },

    async finalizarPartida(
      stats
    ) {
      if (this.modoPrueba) {
        console.log(
          "Partida Grid Dash en modo prueba. No se guarda en BBDD.",
          stats
        );

        this.verifiedScore =
          this.score;

        this.verifiedMoney =
          null;

        return;
      }

      if (
        !this.claveTemporal
      ) {
        return;
      }

      const statsOrdenados =
        Object.keys(stats)
          .sort()
          .reduce(
            (obj, key) => {
              obj[key] =
                stats[key];

              return obj;
            },
            {}
          );

      const statsStr =
        JSON.stringify(
          statsOrdenados
        );

      const hash =
        CryptoJS.HmacSHA256(
          statsStr,
          this.claveTemporal
        ).toString(
          CryptoJS.enc.Hex
        );

      try {
        const response =
          await axios.post(
            "/api/minigames/partida/finalizar",
            {
              id_partida:
                this.idPartida,

              stats,

              hash,
            }
          );

        if (
          response.data.status ===
          "success"
        ) {
          this.verifiedScore =
            response.data.data
              .score;

          this.verifiedMoney =
            response.data.data
              .dinero;

          await Promise.all([
            this.store.refreshStats(),
            this.store.refreshCompany(),
          ]);
        }
      } catch (error) {
        console.error(
          "Error al validar partida",
          error
        );
      }
    },

    resetGame() {
      this.gameState = "MENU";

      this.score = 0;

      this.timeLeft =
        this.maxTime;

      this.verifiedScore =
        null;

      this.verifiedMoney =
        null;

      this.idPartida =
        null;

      this.claveTemporal =
        null;

      this.shieldCharges =
        0;

      this.isSmallJumping =
        false;

      this.isBigJumping =
        false;

      this.isDucking =
        false;

      this.jumpLocked =
        false;

      this.wasDucking =
        false;
    },
  },
};
</script>

<style scoped>
.grid-dash-page {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 18px;
  margin: 0 auto;
}

.grid-dash-wrapper {
  width: 100%;
  max-width: 500px;
  height: 700px;
  position: relative;
  background: #071827;
  border: 8px solid #1a2f46;
  border-radius: 15px;
  overflow: hidden;
  user-select: none;
  font-family: sans-serif;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
}

#game-container {
  width: 100%;
  height: 600px;
  touch-action: none;
  overscroll-behavior: contain;
}

#game-container :deep(canvas) {
  outline: none;
  touch-action: none;
}

.mobile-controls {
  height: 100px;
  background: #101820;
  display: flex;
  justify-content: space-around;
  align-items: center;
  gap: 8px;
  padding: 10px;
  border-top: 2px solid rgba(0, 212, 255, 0.35);
}

.ctrl-btn {
  width: 92px;
  height: 62px;
  font-size: 1.9rem;
  background: #1d3548;
  color: white;
  border: none;
  border-bottom: 5px solid #06101a;
  border-radius: 12px;
  cursor: pointer;
  touch-action: none;
  box-shadow: 0 0 12px rgba(0, 212, 255, 0.25);
  -webkit-tap-highlight-color: transparent;
}

.ctrl-btn.big-jump {
  background: #145c78;
  box-shadow: 0 0 14px rgba(0, 212, 255, 0.45);
}

.ctrl-btn.small-jump {
  background: #1d3548;
}

.ctrl-btn:active {
  transform: translateY(3px);
  border-bottom-width: 2px;
  background: #28506e;
}

.overlay {
  position: absolute;
  inset: 0;
  z-index: 100;
  background: rgba(0, 0, 0, 0.84);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  padding: 24px;
}

.overlay h1 {
  color: #00d4ff;
  font-size: 2.8rem;
  margin-bottom: 10px;
  text-shadow: 0 0 12px rgba(0, 212, 255, 0.7);
}

.overlay p {
  line-height: 1.5;
  max-width: 390px;
}

.test-mode-msg {
  color: #ffaa00;
  font-weight: bold;
  font-size: 0.9rem;
}

.hud {
  position: absolute;
  top: 15px;
  width: 100%;
  display: flex;
  justify-content: space-around;
  gap: 6px;
  z-index: 50;
  pointer-events: none;
  padding: 0 8px;
}

.stat {
  background: rgba(255, 255, 255, 0.92);
  color: #1b2f3f;
  padding: 8px 13px;
  border-radius: 20px;
  font-weight: bold;
  font-size: 0.85rem;
  box-shadow: 0 4px 0 rgba(0, 0, 0, 0.25);
  white-space: nowrap;
}

.battery {
  color: #777;
}

.battery.active {
  color: #006fbf;
}

.shield-stat {
  display: flex;
  align-items: center;
  gap: 8px;
}

.hud-shield-icon {
  width: 18px;
  height: 22px;
  position: relative;
  display: inline-block;
  flex-shrink: 0;
  filter: none;
}

.hud-shield-icon::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, #8c8c8c 0%, #5f5f5f 100%);
  clip-path: polygon(50% 0%, 88% 16%, 88% 56%, 50% 100%, 12% 56%, 12% 16%);
  opacity: 0.75;
}

.hud-shield-check {
  position: absolute;
  left: 50%;
  top: 48%;
  width: 8px;
  height: 5px;
  transform: translate(-50%, -50%) rotate(-45deg);
  border-left: 2px solid rgba(255, 255, 255, 0.45);
  border-bottom: 2px solid rgba(255, 255, 255, 0.45);
  box-sizing: border-box;
  z-index: 2;
}

.hud-shield-icon.active {
  filter: drop-shadow(0 0 6px #00cfff);
}

.hud-shield-icon.active::before {
  background: linear-gradient(180deg, #38d9ff 0%, #0077ff 100%);
  opacity: 1;
}

.hud-shield-icon.active .hud-shield-check {
  border-left-color: #ffffff;
  border-bottom-color: #ffffff;
}

.timer-low {
  color: #e74c3c;
  animation: blink 0.5s infinite alternate;
}

@keyframes blink {
  from {
    opacity: 1;
  }

  to {
    opacity: 0.5;
  }
}

.btn-start {
  background: #00d4ff;
  color: #071827;
  border: none;
  padding: 15px 42px;
  font-size: 1.25rem;
  font-weight: bold;
  border-radius: 30px;
  cursor: pointer;
  margin-top: 20px;
  box-shadow: 0 5px 0 #007c96;
}

.btn-start:active {
  transform: translateY(3px);
  box-shadow: 0 2px 0 #007c96;
}

.final-score {
  font-size: 5rem;
  color: #00d4ff;
  margin: 10px 0;
  text-shadow: 0 0 15px rgba(0, 212, 255, 0.7);
}

.result h2 {
  color: #ffffff;
}

.grid-dash-glossary {
  width: 250px;
  min-height: 700px;
  background: linear-gradient(180deg, #0b1c2b 0%, #071827 100%);
  border: 3px solid #1a2f46;
  border-radius: 15px;
  color: white;
  padding: 16px;
  font-family: sans-serif;
  box-shadow: 0 0 18px rgba(0, 212, 255, 0.16);
}

.glossary-title {
  color: #00d4ff;
  font-size: 1.4rem;
  font-weight: bold;
  margin-bottom: 14px;
  text-align: center;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.6);
}

.glossary-section {
  margin-bottom: 18px;
}

.glossary-section-title {
  font-weight: bold;
  margin-bottom: 8px;
  padding-bottom: 5px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
}

.good-title {
  color: #2ecc71;
}

.bad-title {
  color: #ff4d4d;
}

.glossary-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 8px;
  margin-bottom: 8px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.06);
  border-left: 4px solid rgba(46, 204, 113, 0.8);
}

.glossary-item.bad {
  border-left-color: #ff4d4d;
  background: rgba(255, 77, 77, 0.08);
}

.glossary-item strong {
  display: block;
  font-size: 0.95rem;
}

.glossary-item small {
  display: block;
  color: #c7d7e2;
  font-size: 0.78rem;
  margin-top: 2px;
}

.glossary-icon {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-shadow: 0 0 10px currentColor;
}

.icon-mark {
  position: absolute;
  display: block;
}

.icon-mwh {
  background: #2ecc71;
  color: #2ecc71;
  border-radius: 50%;
}

.mark-mwh {
  width: 16px;
  height: 16px;
}

.mark-mwh::before,
.mark-mwh::after {
  content: "";
  position: absolute;
  background: #ffffff;
  border-radius: 2px;
}

.mark-mwh::before {
  width: 4px;
  height: 16px;
  left: 6px;
  top: 0;
}

.mark-mwh::after {
  width: 16px;
  height: 4px;
  left: 0;
  top: 6px;
}

.icon-casacion {
  background: #f1c40f;
  color: #f1c40f;
  transform: rotate(45deg);
  border-radius: 4px;
}

.mark-casacion {
  width: 16px;
  height: 16px;
  transform: rotate(-45deg);
}

.mark-casacion::before,
.mark-casacion::after {
  content: "";
  position: absolute;
  background: #ffffff;
  border-radius: 2px;
}

.mark-casacion::before {
  width: 4px;
  height: 16px;
  left: 6px;
  top: 0;
}

.mark-casacion::after {
  width: 16px;
  height: 4px;
  left: 0;
  top: 6px;
}

.icon-renewable {
  background: #00ff99;
  color: #00ff99;
  border-radius: 50%;
}

.mark-renewable {
  width: 16px;
  height: 16px;
}

.mark-renewable::before,
.mark-renewable::after {
  content: "";
  position: absolute;
  background: #064635;
  border-radius: 2px;
}

.mark-renewable::before {
  width: 4px;
  height: 16px;
  left: 6px;
  top: 0;
}

.mark-renewable::after {
  width: 16px;
  height: 4px;
  left: 0;
  top: 6px;
}

.icon-battery-shape {
  width: 30px;
  height: 20px;
  background: linear-gradient(180deg, #4aa3ff 0%, #1565c0 100%);
  color: #3498db;
  border-radius: 5px;
  box-shadow: 0 0 10px #3498db;
}

.icon-battery-shape::after {
  content: "";
  position: absolute;
  right: -5px;
  top: 6px;
  width: 5px;
  height: 8px;
  background: #85c1e9;
  border-radius: 2px;
}

.mark-battery {
  width: 16px;
  height: 16px;
}

.mark-battery::before,
.mark-battery::after {
  content: "";
  position: absolute;
  background: #ffffff;
  border-radius: 2px;
}

.mark-battery::before {
  width: 4px;
  height: 16px;
  left: 6px;
  top: 0;
}

.mark-battery::after {
  width: 16px;
  height: 4px;
  left: 0;
  top: 6px;
}

.glossary-item.protection {
  border-left-color: #00cfff;
  background: rgba(0, 207, 255, 0.08);
}

.icon-shield-shape {
  width: 28px;
  height: 32px;
  position: relative;
  color: #00cfff;
  box-shadow: none;
  filter: drop-shadow(0 0 8px #00cfff);
}

.icon-shield-shape::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, #38d9ff 0%, #0077ff 100%);
  clip-path: polygon(50% 0%, 88% 16%, 88% 56%, 50% 100%, 12% 56%, 12% 16%);
}

.mark-shield {
  width: 12px;
  height: 7px;
  left: 50%;
  top: 47%;
  transform: translate(-50%, -50%) rotate(-45deg);
  border-left: 3px solid #ffffff;
  border-bottom: 3px solid #ffffff;
  box-sizing: border-box;
  z-index: 2;
}

.icon-negative-price-shape {
  width: 28px;
  height: 28px;
  position: relative;
  box-shadow: none;
  filter: drop-shadow(0 0 8px #e53935);
}

.icon-negative-price-shape::before {
  content: "";
  position: absolute;
  left: 50%;
  top: 2px;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 14px solid transparent;
  border-right: 14px solid transparent;
  border-bottom: 26px solid #e53935;
}

.mark-negative-price {
  width: 10px;
  height: 3px;
  background: #ffffff;
  border-radius: 2px;
  left: 50%;
  top: 18px;
  transform: translateX(-50%);
  z-index: 2;
}

.icon-imbalance-shape {
  width: 28px;
  height: 28px;
  background: #ff1744;
  color: #ff1744;
  border-radius: 8px;
}

.mark-imbalance {
  width: 16px;
  height: 4px;
  background: #ffffff;
  border-radius: 2px;
}

.icon-restriction-shape {
  width: 32px;
  height: 20px;
  background: #c62828;
  color: #c62828;
  border-radius: 6px;
}

.mark-restriction {
  width: 16px;
  height: 4px;
  background: #ffffff;
  border-radius: 2px;
}

.glossary-help {
  margin-top: 14px;
  padding: 12px;
  border-radius: 12px;
  background: rgba(0, 212, 255, 0.09);
  border: 1px solid rgba(0, 212, 255, 0.25);
  color: #d7f6ff;
  font-size: 0.82rem;
  line-height: 1.4;
}

@media (max-width: 850px) {
  .grid-dash-page {
    flex-direction: column;
    align-items: center;
  }

  .grid-dash-glossary {
    width: 100%;
    max-width: 500px;
    min-height: auto;
  }
}
</style>